import { Map } from 'immutable'
import React from 'react'
import { Action, any, AsyncState, IOWidget, link, mk_widget, Pair } from 'widgets-for-react'
import { DataLayerHandler } from '../../../../../helpers'
import { Id, ImageSize, Link } from '../../../../cms/blocks/types.gen'
import { getClassName } from '../../../../cms/blocks/utils.gen'
import { ImageContainer } from '../../../../cms/blocks/widgets/ImageContainer.gen'
import { TemplateState } from '../../../../cms/page/types.gen'
import { video, VideoProps } from '../../../../video'
import { CustomBlock } from '../../../../wordpress/types_and_parser/custom_blocks.gen'
import { Block } from '../../../../wordpress/types_and_parser/standard_blocks.gen'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { CustomBlockData } from '../types.mod'

export type BSLFeaturedContentBlockProps<customBlockData, extra_images> = {
  kind_media: 'image' | 'video'
  imageLocation: 'left' | 'right'
  image: Id
  image_size: ImageSize<extra_images>
  image_alt_text: string
  video?: VideoProps
  title: string
  intro?: string
  primary_button?: Link | null
  secondary_button?: Link | null
  get_default_data_src: () => ImageSize<extra_images>
  get_default_data_srcset: () => Array<Pair<ImageSize<extra_images>, string>>
  cta_event_name?: string
  cta_event_name_secondary?: string
}

export type BSLFeaturedContentBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  content_classname: string
  header_classname: string
  title_classname: string
  text_classname: string
  intro_classname: string
  footer_classname: string
  clickable_primary_classname: string
  clickable_secondary_classname: string
  figure_classname: string
  image_classname: string
}>

export function BSLFeaturedContentBlock<customBlockData, extra_images>(
  props: BSLFeaturedContentBlockProps<customBlockData, extra_images>,
  options?: { style?: BSLFeaturedContentBlockStyle; key?: string }
): IOWidget<
  TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>,
  Action<TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>>
> {
  return (s: TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>) =>
    mk_widget({
      run: _ => {
        const imageState = s.images.get(props.image)
        return (
          <section
            className={getClassName(options, 'block_classname', 'container')}
            key={options && options.key ? options.key : 'featured-content-block'}
          >
            <div className={getClassName(options, 'inner_classname', 'row')}>
              <div
                className={getClassName(
                  options,
                  'content_classname',
                  `col-md-6 mb-4 order-${props.imageLocation === 'left' ? 2 : 1}`
                )}
              >
                <header className={getClassName(options, 'header_classname', '')}>
                  <h2 className={getClassName(options, 'title_classname', 'mb-3')}>{props.title}</h2>
                </header>
  
                <div className={getClassName(options, 'text_classname', '')}>
                  <p className={getClassName(options, 'intro_classname', '')}>{props.intro}</p>
                </div>
  
                {props.primary_button || props.secondary_button ? (
                  <footer className={getClassName(options, 'footer_classname', '')}>
                    {props.primary_button && (
                      <a
                        onClick={() => {
                          if (props.cta_event_name != undefined && props.cta_event_name != "") {
                            DataLayerHandler({
                              event: "trackEvent", 
                              eventCategory: "ctaClick", 
                              eventAction: "click", 
                              eventLabel: props.cta_event_name
                            })
                          }
                        }}
                        href={props.primary_button.url}
                        target={props.primary_button.target}
                        className={getClassName(options, 'clickable_primary_classname', '')}
                      >
                        {props.primary_button.title}
                      </a>
                    )}
  
                    {props.secondary_button && (
                      <a
                        onClick={() => {
                          if (props.cta_event_name_secondary != undefined && props.cta_event_name_secondary != "") {
                            DataLayerHandler({
                              event: "trackEvent", 
                              eventCategory: "ctaClick", 
                              eventAction: "click", 
                              eventLabel: props.cta_event_name_secondary
                            })
                          }
                        }}
                        href={props.secondary_button.url}
                        target={props.secondary_button.target}
                        className={getClassName(options, 'clickable_secondary_classname', '')}
                      >
                        {props.secondary_button.title}
                      </a>
                    )}
                  </footer>
                ) : null}
              </div>
  
              <div
                className={getClassName(
                  options,
                  'figure_classname',
                  `col-md-6 mb-4 order-${props.imageLocation === 'left' ? 1 : 2}`
                )}
              >
                {props.kind_media === 'image' && s.images.has(props.image)
                  ? ImageContainer(
                      s.images.get(props.image) as AsyncState<Map<ImageSize<extra_images>, string>>,
                      props.image_size,
                      getClassName(options, 'image_classname', 'w-100'),
                      props.image_alt_text,
                      props.get_default_data_src,
                      props.get_default_data_srcset
                    ).run(_ => {})
                  : props.video !== undefined && video('image' in props && s.images.has(props.image) && imageState.kind == 'loaded' ? imageState.value.get(props.image_size || 'full') : '')(props.video).run(() => null)}
              </div>
            </div>
          </section>
        )
      }
    })
}
